import { useState, useEffect } from "react";
import { DailyEventsList } from "@/components/DailyEventsList";
import { CalendarEvent } from "@/Calendar";
import { UnifiedEvent } from "@/components/PaginatedEventsList";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { format, addDays, subDays } from "date-fns";

interface EventsPanelProps {
  selectedDate?: number;
  events: CalendarEvent[];
  isLoading?: boolean;
}

export const EventsPanel = ({ selectedDate, events, isLoading }: EventsPanelProps) => {
  const [selectedEvent, setSelectedEvent] = useState<UnifiedEvent | null>(null);
  const [currentViewDate, setCurrentViewDate] = useState<Date>(new Date());

  // Sync currentViewDate when selectedDate changes from calendar clicks
  useEffect(() => {
    if (selectedDate) {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      // Use UTC to match how Calendar component creates dates
      const newDate = new Date(Date.UTC(currentYear, currentMonth, selectedDate));
      setCurrentViewDate(newDate);
    }
  }, [selectedDate]);

  const handleEventClick = (event: UnifiedEvent) => {
    console.log("EventsPanel: Event clicked:", event);
    setSelectedEvent(event);
  };

  const handlePrevDate = () => {
    setCurrentViewDate(prev => subDays(prev, 1));
  };

  const handleNextDate = () => {
    setCurrentViewDate(prev => addDays(prev, 1));
  };

  const handleTodayClick = () => {
    setCurrentViewDate(new Date());
  };

  return (
    <div className="border rounded-lg bg-background h-full flex flex-col overflow-hidden">
      {/* Date Navigation Header */}
      <div className="flex-none border-b border-border/50 p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-blue-400" />
            <h2 className="font-semibold text-white">Events</h2>
          </div>
          
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrevDate}
              className="h-8 w-8 p-0 hover:bg-white/10"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleTodayClick}
              className={cn(
                "px-3 h-8 text-xs font-medium hover:bg-white/10",
                format(currentViewDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd') && 
                "bg-blue-600/20 text-blue-300"
              )}
            >
              {format(currentViewDate, 'MMM d')}
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNextDate}
              className="h-8 w-8 p-0 hover:bg-white/10"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Events List */}
      <div className="flex-1 overflow-hidden">
        <DailyEventsList
          events={events}
          selectedEvent={selectedEvent}
          onEventClick={handleEventClick}
          selectedDate={selectedDate}
          isLoading={isLoading}
          viewDate={currentViewDate}
        />
      </div>
    </div>
  );
};